import { generateCodeVerifier, OAuth2Client, OAuth2Fetch } from "@badgateway/oauth2-client";
import { Database, Table } from "../types";
import { SupabaseClient } from "@supabase/supabase-js";
import { nanoid } from "nanoid";

export type OauthConfig = {
  access_token: string;
  refresh_token: string;
  access_expires_at: string;
};

export class BaseOauthClient {
  id: string = "";
  provider: string = "";
  teamId: string;

  baseUrl: string = "";
  authorizationEndpoint: string = "";
  tokenEndpoint: string = "";

  scopes: string[] = [];
  extraParams: Record<string, string> = {};
  authenticationMethod!: string;

  clientId: string = "";
  clientSecret: string = "";
  scope: string = "";

  refreshToken: string = "";
  oauthClient!: OAuth2Client;
  fetcher!: OAuth2Fetch;
  supabaseClient: SupabaseClient<Database>;

  constructor(
    team: string,
    supabaseClient: SupabaseClient<Database>,
    id: string,
    credentials?: { clientId: string; clientSecret: string; }
  ) {
    this.teamId = team;
    this.supabaseClient = supabaseClient;
    this.id = id || "";
    if (credentials) {
      this.clientId = credentials?.clientId || "";
      this.clientSecret = credentials?.clientSecret || "";
    }

    this.initializeOauthClient();

  }

  initializeOauthClient() {

    this.oauthClient = new OAuth2Client({
      clientId: this.clientId,
      clientSecret: this.clientSecret,
      authorizationEndpoint: this.authorizationEndpoint,
      tokenEndpoint: this.tokenEndpoint,
      server: this.baseUrl,
      authenticationMethod: (this.authenticationMethod || undefined) as any,

    });

    this.fetcher = new OAuth2Fetch({
      client: this.oauthClient,

      getNewToken: async () => {
        if (!this.id || this.id === "") return null;

        let { data, error } = await this.supabaseClient
          .from(Table.Integrations)
          .select()
          .eq("id", this.id)
          .maybeSingle();

        if (error || !data || !data.config) {
          return null;
        }
        const config = data.config as OauthConfig;
        let new_token = await this.oauthClient.refreshToken({
          accessToken: config.access_token,
          refreshToken: config.refresh_token,
          expiresAt: new Date(config.access_expires_at).getTime()
        });

        return new_token;
      },

      /**
       * This function is called whenever the active token changes. Using this is
       * optional, but it may be used to (for example) put the token in off-line
       * storage for later usage.
       */
      storeToken: async (token) => {

        if (!this.supabaseClient) return null;

        let { error } = await this.supabaseClient.from(Table.Integrations).update(
          {
            provider: this.provider,
            team: this.teamId,
            config: {
              access_token: token.accessToken,
              refresh_token: token.refreshToken || this.refreshToken,
              access_expires_at: new Date(
                token.expiresAt || new Date(2040, 1, 1).getTime()
              ).toISOString(),
            },
          }
        ).eq("id", this.id);
      },

      /**
       * Also an optional feature. Implement this if you want the wrapper to try a
       * stored token before attempting a full re-authentication.
       *
       * This function may be async. Return null if there was no token.
       */
      getStoredToken: async () => {

        if (!this.supabaseClient) return null;
        if (!this.id || this.id === "") return null;

        let { data, error } = await this.supabaseClient
          .from(Table.Integrations)
          .select()
          .eq("id", this.id)
          .maybeSingle();

        if (error || !data || !data.config) {
          return null;
        }
        const config = data.config as OauthConfig;
        this.refreshToken = config.refresh_token;
        return {
          accessToken: config.access_token,
          refreshToken: config.refresh_token,
          expiresAt: new Date(config.access_expires_at).getTime()
        };
      }

    });
  }

  async startAuthFlow() {
    const state = nanoid(16);

    const codeVerifier = await generateCodeVerifier();

    // Generate the url that will be used for the consent dialog.
    const authorizeUrl = await this.oauthClient.authorizationCode.getAuthorizeUri({
      redirectUri: `${(process.env.NEXT_PUBLIC_DASHBOARD_URL || "")}/api/internal/oauth/${this.provider}/redirect`,
      scope: this.scopes,
      extraParams: this.extraParams,
      state: state,
      codeVerifier: codeVerifier
    });

    return {
      url: authorizeUrl,
      code_verifier: codeVerifier,
      state: state,
    };
  }

  async completeAuthFlow(req: any) {
    let code = req.nextUrl.href;
    let code_verifier = req.cookies.get("code_verifier")?.value;
    let state = req.nextUrl.searchParams.get("state");

    if (!code) {
      throw new Error("No code found in URL");
    }

    if (!this.teamId) {
      throw new Error("Not connected to an team");
    }

    let r: any;
    try {

      r = await this.oauthClient.authorizationCode.getTokenFromCodeRedirect(
        code,
        {
          redirectUri: `${(process.env.NEXT_PUBLIC_DASHBOARD_URL || "")}/api/internal/oauth/${this.provider}/redirect`,
          codeVerifier: code_verifier || undefined,
          state: state || undefined,
        });
    } catch (e: any) {
      console.log("Failed to get token from code: ", e);
      throw new Error("Failed to get token from code: " + e.message);
    }

    let { data, error } = await this.supabaseClient.from(Table.Integrations).update(
      {
        status: "healthy",
        config: {
          access_token: r.accessToken,
          refresh_token: r.refreshToken,
          access_expires_at: new Date(r.expiresAt).toISOString(),
        },
      }
    ).eq("id", this.id).select().single();

    this.refreshToken = r.refreshToken;
    if (error) {
      throw new Error("Failed to save integration to DB: " + error.message);
    }
    return { success: true, ...r };
  }

}
